import React, { useState } from "react";
import {
  Button,
  Col,
  message,
  Modal,
  Row,
  Skeleton,
  Switch,
  Table,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";

import { useFetchConstant } from "apis/constant";
import { patchAdviseeCashInflow } from "apis/advisee";

import InfoCard from "components/InfoCard/InfoCard";
import SectionContainer from "components/SectionContainer/SectionContainer";
import Title from "components/Title/Title";

import { constructDisplayPrice, getLabelOfConstant } from "utils/general";

import AddSourceModal from "./components/AddSourceModal/AddSourceModal";
import OverrideTotalModal from "../OverrideTotalModal/OverrideTotalModal";
import {
  OverridingLabel,
  OverridingRow,
  SummaryValueText,
} from "./CashInflows.styles";

import EditableTable from "./components/EditableTable/EditableTable";
import Locale from "react-intl-universal";

const { confirm } = Modal;

const tipsDescription = " ";
// <span>
//   By overriding the <b>Total CashInflows</b>, all the calculations will be
//   based on the overrided value. You can <b>switch back to auto-calculate</b>{" "}
//   to be based on the sources added.
// </span>

const SummaryCashInflowInfoCard = ({ title, totalValue }) => {
  return (
    <InfoCard
      title={title}
      tipsDescription={
        <p>
          {Locale.get("cashFlowSummary.cashInflowSummary.tipsDescLine_1")}
          <b>{Locale.get("cashFlowSummary.tCashInflow")}</b>{" "}
          {Locale.get("cashFlowSummary.cashInflowSummary.tipsDescLine_2")}
          <b>
            {Locale.get("cashFlowSummary.cashInflowSummary.tipsDescLine_3")}
          </b>
          {Locale.get("cashFlowSummary.cashInflowSummary.tipsDescLine_4")}
        </p>
      }
    >
      <SummaryValueText>
        {constructDisplayPrice(totalValue || 0, false)}
      </SummaryValueText>
    </InfoCard>
  );
};

const formatSourcesForTable = (sources = []) =>
  sources.map((source) => ({
    ...source,
    self: source.amount && source.amount.self,
    spouse: source.amount && source.amount.spouse,
  }));

const getSourcesTableColumns = (incomeTypes) => {
  return [
    {
      title: Locale.get("cashFlowSummary.cashInflowSummary.source"),
      dataIndex: "type",
      key: "type",
      render: (value, record) => (
        <span>
          {value ? getLabelOfConstant(value, incomeTypes) : "-"}
          {record.label ? ` - ${record.label}` : ""}
        </span>
      ),
    },
    {
      title: Locale.get("cashflowSummary.self"),
      dataIndex: "self",
      key: "self",
      render: (value) => (
        <span>{value ? constructDisplayPrice(value, false) : "-"}</span>
      ),
    },
    {
      title: Locale.get("cashflowSummary.spouse"),
      dataIndex: "spouse",
      key: "spouse",
      render: (value) => (
        <span>{value ? constructDisplayPrice(value, false) : "-"}</span>
      ),
    },
    {
      title: Locale.get("cashflowSummary.total"),
      render: (value, record) => {
        const selfAmt = Number(record.self) || 0;
        const spouseAmt = Number(record.spouse) || 0;
        const total = selfAmt + spouseAmt;
        return <span>{total ? constructDisplayPrice(total, false) : "-"}</span>;
      },
    },
  ];
};

const CashInflows = ({ sources, cashInflowSummary, incomeTemplate }) => {
  const { id: adviseeId } = useParams();
  const [showAddNewCashInflowModal, setShowAddNewCashInflowModal] = useState(
    false
  );
  const [showOverridingTotalModal, setShowOverridingTotalModal] = useState(
    false
  );
  const {
    isLoading: isIncomeTypesLoading,
    selection: incomeTypes,
  } = useFetchConstant("incomeTypes");
  const isConstantsLoading = isIncomeTypesLoading;
  const [selectedSourceIndex, setSelectedSourceIndex] = useState(-1);

  const handleOnOverrideTotalSwitchChange = (checked) => {
    if (checked) {
      setShowOverridingTotalModal(checked);
    } else {
      confirm({
        title: Locale.get("cashFlowSummary.cashInflowSummary.confirmMsg"),
        content: Locale.get(
          "cashFlowSummary.cashInflowSummary.confirmMsgContent"
        ),
        okText: Locale.get("cashFlowSummary.yes"),
        onOk() {
          const payload = {
            overridingTotal: {
              self: 0,
              spouse: 0,
            },
          };
          patchAdviseeCashInflow(adviseeId, payload)
            .then((res) => {
              message.success(Locale.get("cashFlowSummary.updateSuccess"));
            })
            .catch((ex) => {
              message.error(Locale.get("cashFlowSummary.errMsgCashflow"));
            });
        },
        onCancel() {},
      });
    }
  };

  const updateCashInflowSources = (
    sources,
    successMessage = Locale.get("cashFlowSummary.updateSuccess")
  ) => {
    patchAdviseeCashInflow(adviseeId, { sources })
      .then((res) => {
        message.success(successMessage);
        setSelectedSourceIndex(-1);
        setShowAddNewCashInflowModal(false);
      })
      .catch((ex) => {
        message.error(Locale.get("cashFlowSummary.errMsgCashflow"));
      });
  };

  const handleOnOverridingTotalModalOkButtonClick = (values) => {
    patchAdviseeCashInflow(adviseeId, values)
      .then((res) => {
        message.success(Locale.get("cashFlowSummary.updateSuccess"));
        setShowOverridingTotalModal(false);
      })
      .catch((ex) => {
        message.error(Locale.get("cashFlowSummary.errMsgCashflow"));
      });
  };

  // const handleOnSourcesRowSelect = index => {
  //   setSelectedSourceIndex(index);
  //   setShowAddNewCashInflowModal(true);
  // };

  const handleOnAddCashFlowModalOkButtonClick = (values) => {
    if (selectedSourceIndex > -1) {
      let editedSources = [...sources];
      editedSources[selectedSourceIndex] = values;
      updateCashInflowSources(editedSources);
      setSelectedSourceIndex(-1);
    } else {
      const newSources = [...sources, values];
      updateCashInflowSources(
        newSources,
        Locale.get("cashFlowSummary.newSourceAdded")
      );
    }
  };

  const handleOnAddCashFlowModalRemoveButtonClick = () => {
    const newSources = [...sources];
    newSources.splice(selectedSourceIndex, 1);
    updateCashInflowSources(
      newSources,
      Locale.get("cashFlowSummary.removeSource")
    );
  };

  const handleOnAddCashFlowModalCancelButtonClick = () => {
    setSelectedSourceIndex(-1);
    setShowAddNewCashInflowModal(false);
  };

  return (
    <>
      <Row justify="space-between" align="middle">
        <Title>
          {Locale.get("cashFlowSummary.cashInflowSummary.cashInflow")}
        </Title>
        {/* <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setShowAddNewCashInflowModal(true)}
        >
          Add new source
        </Button> */}
      </Row>
      {isConstantsLoading ? (
        <Skeleton active />
      ) : (
        <>
          <SectionContainer>
            <OverridingRow align="middle">
              <OverridingLabel>
                {Locale.get("cashFlowSummary.cashInflowSummary.overrideLabel")}
              </OverridingLabel>
              <Switch
                checked={cashInflowSummary.isOverridden}
                onChange={handleOnOverrideTotalSwitchChange}
                style={{ marginLeft: "8px" }}
                size="small"
              />
            </OverridingRow>
            <Row gutter={16}>
              <Col span={14} xl={8}>
                <SummaryCashInflowInfoCard
                  isTotalOverridden={cashInflowSummary.isOverridden}
                  onChange={(checked) => setShowOverridingTotalModal(checked)}
                  title={Locale.get(
                    "cashFlowSummary.cashInflowSummary.totalIncome"
                  )}
                  totalValue={cashInflowSummary.totalValue}
                />
              </Col>
            </Row>
          </SectionContainer>
          {/* <Table
            columns={getSourcesTableColumns(incomeTypes)}
            dataSource={formatSourcesForTable(sources)}
            onRow={(record, index) => {
              return {
                onClick: () => handleOnSourcesRowSelect(index)
              };
            }}
            pagination={false}
            rowKey={record => record._id}
          /> */}

          <EditableTable
            data={formatSourcesForTable(sources)}
            isLoading={isIncomeTypesLoading}
            incomeTemplate={incomeTemplate}
          />
        </>
      )}
      {showAddNewCashInflowModal && (
        <AddSourceModal
          defaultValues={
            selectedSourceIndex > -1 && sources[selectedSourceIndex]
          }
          isLoading={isConstantsLoading}
          onOk={handleOnAddCashFlowModalOkButtonClick}
          onClose={handleOnAddCashFlowModalCancelButtonClick}
          onRemove={handleOnAddCashFlowModalRemoveButtonClick}
          types={incomeTypes}
          visible={showAddNewCashInflowModal}
        />
      )}
      {showOverridingTotalModal && (
        <OverrideTotalModal
          defaultValues={cashInflowSummary}
          onOk={handleOnOverridingTotalModalOkButtonClick}
          onCancel={() => setShowOverridingTotalModal(false)}
          title="Override Total Amount of Incomes"
          visible={showOverridingTotalModal}
        />
      )}
    </>
  );
};

export default CashInflows;
